import {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {Badge, Button, Card, Grid, Paper, Table, Text, Title} from '@mantine/core';
import {showNotification} from '@mantine/notifications';
import useTranslation from 'src/i18next.config';
import {useOrganizationService} from 'src/services/organization';
import {useUserService} from 'src/services';
import useTosService from 'src/services/tos';
import {Organization} from 'src/types/models/Organization';
import {User} from 'src/types/models/User';
import {ToS} from 'src/types/models/ToS';
import {ERoutes} from 'src/types/enum/routes.enum';
import {humanFileSize} from 'src/helpers/file.helper';
import ProgressCard from 'src/components/core/ProgressCard';
import ConfirmationModal from '../../core/ConfirmationModals';

export function OrganizationDetail() {
    const {t} = useTranslation();
    const {id} = useParams();
    const navigate = useNavigate();
    const organizationService = useOrganizationService();
    const userService = useUserService();
    const tosService = useTosService();

    const [organization, setOrganization] = useState<Organization | null>(null);
    const [users, setUsers] = useState<User[]>([]);
    const [tosList, setTosList] = useState<ToS[]>([]);
    const [isModalOpen, setIsModalOpen] = useState(false);

    useEffect(() => {
        if (!id) return;
        organizationService.getOrganization(
            {
                error: error => {
                    console.error(error);
                },
                success: res => {
                    setOrganization(res);
                },
            },
            id
        );
        userService.getUsersByOrganization(
            {
                error: error => {
                    console.error(error);
                },
                success: res => {
                    return res.data;
                },
            },
            id
        ).then((usersList: User[]) => {
            setUsers(usersList);
        });
        tosService.getTosList({
            error: () => { /* no-op */ },
            success: (fetchedTosList: ToS[]) => {
                setTosList(fetchedTosList);
            },
        });
    }, [id]);

    function cleanDisplayNumber(value: number): number {
        if (value % 1) {
            return parseFloat(value.toFixed(2));
        } else {
            return value;
        }
    }

    function deleteOrganization() {
        if (!id) return;
        organizationService.deleteOrganization(
            {
                error: error => {
                    showNotification({
                        title: t('organization.deleteOrganizationNotification'),
                        message: error ? error.message : t('organization.deleteOrganizationErrorMessage'),
                        color: 'red',
                    });
                },
                success: () => {
                    showNotification({
                        title: t('organization.deleteOrganizationNotification'),
                        message: t('organization.deleteOrganizationSuccessMessage'),
                        color: 'green',
                    });
                    navigate(ERoutes.ADMIN_ORGANIZATIONS);
                },
            },
            id
        );
    }

    if (!organization) {
        return <></>;
    }

    function displayUsedTime() {
        if (!organization) return null;
        if (organization.enabledExecutionTime && organization.maxExecutionTime > 0) {
            return (
                <Grid.Col span={{base: 12, sm: 6}}>
                    <ProgressCard
                        displayAsPercentage={false}
                        current={cleanDisplayNumber(organization.usedExecutionTime / 60)}
                        displayName={t('userLimits.usedExecutionTime')}
                        max={organization.maxExecutionTime / 60}
                    />
                </Grid.Col>
            );
        }
        if (organization.usedExecutionTime != null) {
            return (
                <Grid.Col span={{base: 12, sm: 6}}>
                    <Card withBorder radius="md" p="lg">
                        <Text fz="lg">{t('userLimits.usedExecutionTime')}</Text>
                        <Text fz="lg" fw={500}>{cleanDisplayNumber(organization.usedExecutionTime / 60)} min</Text>
                    </Card>
                </Grid.Col>
            );
        }
        return null;
    }

    function displayUsedExecutions() {
        if (!organization) return null;
        if (organization.enabledMaxExecutions && organization.maxExecutions > 0) {
            return (
                <Grid.Col span={{base: 12, sm: 6}}>
                    <ProgressCard
                        displayAsPercentage={false}
                        current={organization.usedExecutions}
                        displayName={t('userLimits.usedExecutions')}
                        max={organization.maxExecutions}
                    />
                </Grid.Col>
            );
        }
        if (organization.usedExecutions != null) {
            return (
                <Grid.Col span={{base: 12, sm: 6}}>
                    <Card withBorder radius="md" p="lg">
                        <Text fz="lg">{t('userLimits.usedExecutions')}</Text>
                        <Text fz="lg" fw={500}>{organization.usedExecutions}</Text>
                    </Card>
                </Grid.Col>
            );
        }
        return null;
    }

    function displayMaxNumberOfUsers() {
        if (!organization) return null;
        if (organization.enabledMaxNumberOfUsers && organization.maxNumberOfUser > 0) {
            return (
                <Grid.Col span={{base: 12, sm: 6}}>
                    <ProgressCard
                        displayAsPercentage={false}
                        current={users.length}
                        displayName={t('userLimits.userNumber')}
                        max={organization.maxNumberOfUser}
                    />
                </Grid.Col>
            );
        }
        return null;
    }

    function displayTimeout() {
        if (!organization) return null;
        if (organization.enabledMaxTimeout) {
            return (
                <Grid.Col span={{base: 12, sm: 6}}>
                    <Card withBorder radius="md" p="lg">
                        <Text fz="lg">{t('userLimits.maxTimeout')}</Text>
                        <Text fz="lg" fw={500}>
                            {organization.maxTimeout} {t('userLimits.minutes')}
                        </Text>
                    </Card>
                </Grid.Col>
            );
        }
        return null;
    }

    function displayMaxFileSizePerExecution() {
        if (!organization) return null;
        if (organization.enabledMaxFilesSizePerExecution) {
            return (
                <Grid.Col span={{base: 12, sm: 6}}>
                    <Card withBorder radius="md" p="lg">
                        <Text fz="lg">{t('userLimits.maxFilesSizePerExecution')}</Text>
                        <Text fz="lg" fw={500}>
                            {humanFileSize(organization.maxFilesSizePerExecution)}
                        </Text>
                    </Card>
                </Grid.Col>
            );
        }
        return null;
    }

    function displayTotalFilesSizeAllowedPerUser() {
        if (!organization) return null;
        if (organization.enabledTotalFilesSizeAllowedPerUser) {
            return (
                <Grid.Col span={{base: 12, sm: 6}}>
                    <Card withBorder radius="md" p="lg">
                        <Text fz="lg">{t('totalFilesSizeAllowedPerUser')}</Text>
                        <Text fz="lg" fw={500}>
                            {humanFileSize(organization.totalFilesSizeAllowedPerUser)}
                        </Text>
                    </Card>
                </Grid.Col>
            );
        }
        return null;
    }

    function getLatestTos(): ToS | null {
        if (tosList.length === 0) return null;
        return tosList.reduce((prev, current) =>
            (prev.version ?? 0) > (current.version ?? 0) ? prev : current
        );
    }

    function isLatestTosApproved(): boolean {
        const latest = getLatestTos();
        if (!latest) return false;
        return (organization?.validatedToS ?? []).includes(latest._id);
    }

    const userRows = users.map((user: User) => (
        <Table.Tr key={user._id}>
            <Table.Td>{user.firstName}</Table.Td>
            <Table.Td>{user.lastName}</Table.Td>
            <Table.Td>{user.email}</Table.Td>
            <Table.Td>{t(`roles.${user.role}`)}</Table.Td>
            <Table.Td>{user.description}</Table.Td>
            <Table.Td>
                <Button
                    variant="outline"
                    onClick={() => {
                        navigate(ERoutes.UPDATE_USER, {
                            state: {userToUpdate: user},
                        });
                    }}
                >
                    {t('user.editUser')}
                </Button>
            </Table.Td>
        </Table.Tr>
    ));

    return (
        <>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20}}>
                <Title order={2}>{organization.name}</Title>
                <div style={{display: 'flex', gap: '0.5rem'}}>
                    <Button
                        color="cyan.8"
                        variant="outline"
                        onClick={() => navigate(`/admin/organizations/${id}/edit`)}
                    >
                        {t('organization.editOrganizationBtn')}
                    </Button>
                    <Button
                        color="red"
                        variant="outline"
                        onClick={() => setIsModalOpen(true)}
                    >
                        {t('organization.deleteOrganization')}
                    </Button>
                </div>
            </div>

            <Title order={3} style={{marginBottom: 10}}>{t('organization.usageAndLimits')}</Title>
            <Grid style={{marginBottom: 30}}>
                {displayUsedTime()}
                {displayUsedExecutions()}
                {displayMaxNumberOfUsers()}
                {displayTimeout()}
                {displayMaxFileSizePerExecution()}
                {displayTotalFilesSizeAllowedPerUser()}
            </Grid>

            {tosList.length > 0 && (
                <>
                    <Title order={3} style={{marginBottom: 10}}>{t('tos.tos')}</Title>
                    <Card withBorder radius="md" p="lg" style={{marginBottom: 30}}>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                            <div>
                                <Text fz="sm" c="dimmed">{t('tos.version')} {getLatestTos()?.version ?? '—'}</Text>
                                <Text fz="sm" c="dimmed">
                                    {t('tos.intoEffectAt')}: {getLatestTos()?.intoEffectAt
                                        ? new Date(getLatestTos()!.intoEffectAt).toLocaleDateString()
                                        : '—'}
                                </Text>
                            </div>
                            <Badge size="lg" color={isLatestTosApproved() ? 'green' : 'red'}>
                                {isLatestTosApproved() ? t('tos.approved') : t('organization.tosNotApproved')}
                            </Badge>
                        </div>
                    </Card>
                </>
            )}

            <Title order={3} style={{marginBottom: 10}}>{t('organization.users')}</Title>
            <Paper shadow="md" style={{width: 'fit-content', margin: 'auto'}}>
                <Table.ScrollContainer minWidth={700}>
                    <Table highlightOnHover>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th style={{minWidth: '100px'}}>{t('firstName')}</Table.Th>
                                <Table.Th style={{minWidth: '100px'}}>{t('lastName')}</Table.Th>
                                <Table.Th>{t('email')}</Table.Th>
                                <Table.Th style={{minWidth: '140px'}}>{t('role')}</Table.Th>
                                <Table.Th>{t('description')}</Table.Th>
                                <Table.Th>{}</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>{userRows}</Table.Tbody>
                    </Table>
                </Table.ScrollContainer>
            </Paper>

            <ConfirmationModal
                isOpen={isModalOpen}
                title={t('organization.deleteOrganization')}
                text={t('areYouSure')}
                onClose={() => setIsModalOpen(false)}
                onConfirmation={() => {
                    setIsModalOpen(false);
                    deleteOrganization();
                }}
            />
        </>
    );
}
